import { useState, useMemo } from 'react';
import {
  TextInput,
  SimpleGrid,
  Group,
  Chip,
  Stack,
  Skeleton,
  Paper,
  Select,
  MultiSelect,
  NumberInput,
  Switch,
  Button,
  Badge,
  Collapse,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch, IconFilter } from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import ProductCard from '@/components/ProductCard';
import EmptyState from '@/components/EmptyState';
import { useProducts, useProductCategories, useFilterOptions } from '@/hooks/usePortalApi';
import type { CatalogFilters } from '@/types';

interface FilterState {
  priceMin: string;
  priceMax: string;
  brand: string;
  countryOfOrigin: string;
  usdaGrade: string;
  excludeAllergens: string[];
  certifications: string[];
  minShelfLife: string;
  isOrganic: boolean;
  isFeatured: boolean;
  isCatchWeight: boolean;
  inStockOnly: boolean;
}

const INITIAL_FILTERS: FilterState = {
  priceMin: '',
  priceMax: '',
  brand: '',
  countryOfOrigin: '',
  usdaGrade: '',
  excludeAllergens: [],
  certifications: [],
  minShelfLife: '',
  isOrganic: false,
  isFeatured: false,
  isCatchWeight: false,
  inStockOnly: false,
};

const SORT_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'name_asc', label: 'Name A-Z' },
  { value: 'name_desc', label: 'Name Z-A' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'popularity', label: 'Popularity' },
];

export default function Catalog() {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string>('');
  const [sortBy, setSortBy] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [debouncedSearch] = useDebouncedValue(search, 300);

  const { data: categories } = useProductCategories();
  const { data: filterOptions } = useFilterOptions();

  // Count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.priceMin) count++;
    if (filters.priceMax) count++;
    if (filters.brand) count++;
    if (filters.countryOfOrigin) count++;
    if (filters.usdaGrade) count++;
    if (filters.excludeAllergens.length) count++;
    if (filters.certifications.length) count++;
    if (filters.minShelfLife) count++;
    if (filters.isOrganic) count++;
    if (filters.isFeatured) count++;
    if (filters.isCatchWeight) count++;
    if (filters.inStockOnly) count++;
    return count;
  }, [filters]);

  // Build API params from filter state
  const apiParams: CatalogFilters = useMemo(() => {
    const params: CatalogFilters = { limit: 50 };
    if (debouncedSearch) params.search = debouncedSearch;
    if (category) params.category = category;
    if (sortBy) params.sort_by = sortBy;
    if (filters.priceMin) params.price_min = parseFloat(filters.priceMin);
    if (filters.priceMax) params.price_max = parseFloat(filters.priceMax);
    if (filters.isOrganic) params.is_organic = true;
    if (filters.isFeatured) params.is_featured = true;
    if (filters.isCatchWeight) params.is_catch_weight = true;
    if (filters.inStockOnly) params.in_stock_only = true;
    if (filters.brand) params.brand = filters.brand;
    if (filters.countryOfOrigin) params.country_of_origin = filters.countryOfOrigin;
    if (filters.usdaGrade) params.usda_grade = filters.usdaGrade;
    if (filters.minShelfLife) params.min_shelf_life = parseInt(filters.minShelfLife, 10);
    if (filters.excludeAllergens.length)
      params.exclude_allergens = filters.excludeAllergens.join(',');
    if (filters.certifications.length)
      params.certifications = filters.certifications.join(',');
    return params;
  }, [debouncedSearch, category, sortBy, filters]);

  const { data: productsData, isLoading } = useProducts(apiParams);
  const products = productsData?.items ?? [];

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setSortBy('');
  };

  return (
    <Stack gap="lg">
      <PageHeader title="Shop Products" description="Browse our catalog and add items to your cart" />

      {/* Search + Category filters */}
      <Paper p="md">
        <Stack gap="sm">
          <TextInput
            placeholder="Search products..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          {categories && categories.length > 0 && (
            <Group gap="xs">
              <Chip
                checked={category === ''}
                onChange={() => setCategory('')}
                variant="light"
                color="brand"
              >
                All
              </Chip>
              {categories.map((cat) => (
                <Chip
                  key={cat.slug}
                  checked={category === cat.name}
                  onChange={() => setCategory(category === cat.name ? '' : cat.name)}
                  variant="light"
                  color="brand"
                >
                  {cat.name} ({cat.count})
                </Chip>
              ))}
            </Group>
          )}
        </Stack>
      </Paper>

      {/* Sort + Filter toggle row */}
      <Group justify="space-between">
        <Group gap="sm">
          <Select
            placeholder="Sort by"
            data={SORT_OPTIONS}
            value={sortBy}
            onChange={(v) => setSortBy(v ?? '')}
            w={200}
            size="sm"
            clearable
          />
          {activeFilterCount > 0 && (
            <Badge size="lg" variant="light" color="brand">
              {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''} active
            </Badge>
          )}
        </Group>
        <Button
          variant={showFilters ? 'filled' : 'light'}
          leftSection={<IconFilter size={16} />}
          onClick={() => setShowFilters((v) => !v)}
          size="sm"
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </Group>

      {/* Collapsible filter panel */}
      <Collapse in={showFilters}>
        <Paper p="md" withBorder>
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
              {/* Price Range */}
              <Stack gap={4}>
                <NumberInput
                  label="Min Price ($)"
                  placeholder="0.00"
                  value={filters.priceMin ? parseFloat(filters.priceMin) : ''}
                  onChange={(v) => updateFilter('priceMin', v ? String(v) : '')}
                  min={0}
                  decimalScale={2}
                  size="sm"
                />
                <NumberInput
                  label="Max Price ($)"
                  placeholder="No limit"
                  value={filters.priceMax ? parseFloat(filters.priceMax) : ''}
                  onChange={(v) => updateFilter('priceMax', v ? String(v) : '')}
                  min={0}
                  decimalScale={2}
                  size="sm"
                />
              </Stack>

              {/* Brand */}
              <Select
                label="Brand"
                placeholder="Any brand"
                data={filterOptions?.brands ?? []}
                value={filters.brand || null}
                onChange={(v) => updateFilter('brand', v ?? '')}
                searchable
                clearable
                size="sm"
              />

              {/* Country of Origin */}
              <Select
                label="Country of Origin"
                placeholder="Any country"
                data={filterOptions?.countries ?? []}
                value={filters.countryOfOrigin || null}
                onChange={(v) => updateFilter('countryOfOrigin', v ?? '')}
                searchable
                clearable
                size="sm"
              />

              {/* USDA Grade */}
              <Select
                label="USDA Grade"
                placeholder="Any grade"
                data={filterOptions?.usda_grades ?? []}
                value={filters.usdaGrade || null}
                onChange={(v) => updateFilter('usdaGrade', v ?? '')}
                clearable
                size="sm"
              />

              {/* Exclude Allergens */}
              <MultiSelect
                label="Exclude Allergens"
                placeholder="Select allergens"
                data={filterOptions?.allergens ?? []}
                value={filters.excludeAllergens}
                onChange={(v) => updateFilter('excludeAllergens', v)}
                searchable
                clearable
                size="sm"
              />

              {/* Certifications */}
              <MultiSelect
                label="Certifications"
                placeholder="Select certifications"
                data={filterOptions?.certifications ?? []}
                value={filters.certifications}
                onChange={(v) => updateFilter('certifications', v)}
                searchable
                clearable
                size="sm"
              />

              {/* Min Shelf Life */}
              <NumberInput
                label="Min Shelf Life (days)"
                placeholder="Any"
                value={filters.minShelfLife ? parseInt(filters.minShelfLife, 10) : ''}
                onChange={(v) => updateFilter('minShelfLife', v ? String(v) : '')}
                min={0}
                size="sm"
              />

              {/* Quick Filter Switches */}
              <Stack gap="xs" justify="center">
                <Switch
                  label="Organic Only"
                  checked={filters.isOrganic}
                  onChange={(e) => updateFilter('isOrganic', e.currentTarget.checked)}
                  size="sm"
                />
                <Switch
                  label="Featured"
                  checked={filters.isFeatured}
                  onChange={(e) => updateFilter('isFeatured', e.currentTarget.checked)}
                  size="sm"
                />
                <Switch
                  label="In Stock Only"
                  checked={filters.inStockOnly}
                  onChange={(e) => updateFilter('inStockOnly', e.currentTarget.checked)}
                  size="sm"
                />
                <Switch
                  label="Catch Weight"
                  checked={filters.isCatchWeight}
                  onChange={(e) => updateFilter('isCatchWeight', e.currentTarget.checked)}
                  size="sm"
                />
              </Stack>
            </SimpleGrid>

            <Group justify="flex-end">
              <Button
                variant="subtle"
                size="sm"
                onClick={clearFilters}
                disabled={activeFilterCount === 0 && !sortBy}
              >
                Clear All Filters
              </Button>
            </Group>
          </Stack>
        </Paper>
      </Collapse>

      {/* Product grid */}
      {isLoading ? (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} height={160} radius="md" />
          ))}
        </SimpleGrid>
      ) : products.length === 0 ? (
        <EmptyState
          title="No products found"
          description={search ? 'Try a different search term' : 'No products available in this category'}
        />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </SimpleGrid>
      )}
    </Stack>
  );
}
